import React from 'react'
import { Building2, ChevronDown, HelpCircle, MoveHorizontal, UserRound } from 'lucide-react'
import useStore, { PLAN_SLOTS, resolveActivePlanKey } from '@/stores/useStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Expandable } from '@/components/ui/expandable'
import { cn, formatCurrency, formatPercent } from '@/lib/utils'
import { safeHarborLabel } from '@/lib/safeHarbor'
import { buildScreenRemediation } from '@/lib/remediation'
import { navigateToPlanSection } from '@/lib/planSectionNavigation'
// Screen display state lives in lib so the Checks board, these compare tables,
// the CFO view, and PDF export all describe a plan identically.
import {
  buildAcpScreen,
  buildAdpScreen,
  buildCashBalanceAllocationScreen,
  buildCoverageScreen,
  buildCrossTestingScreen,
  buildTopHeavyMinScreen,
  buildTopHeavyScreen,
} from '@/lib/complianceScreens'
import { PlanReadinessSummary } from '@/components/status/PlanReadinessSummary'
import { isCashBalanceEnabled } from '@/lib/planDesign'


/**
 * Direction-only change vs Plan A: green ▲ = higher, red ▼ = lower, muted "–"
 * when unchanged. No good/bad judgment, so it reads the same on every metric.
 * Carries no margin so callers control spacing.
 */
function DeltaText({ value, format = 'currency' }) {
  if (!value) return <span className="whitespace-nowrap font-medium uppercase tracking-wide text-muted-foreground">No change</span>
  const higher = value > 0
  const magnitude = format === 'percent' ? formatPercent(Math.abs(value))
    : format === 'number' ? Math.abs(value).toLocaleString()
    : formatCurrency(Math.abs(value))
  return (
    <span className={cn('whitespace-nowrap tabular-nums', higher ? 'text-emerald-700' : 'text-red-700')}>
      {higher ? '▲' : '▼'} {magnitude}
    </span>
  )
}

// Compact per-cell variant: a zero delta reads as a quiet "same" instead of
// the louder "No change" used by the standalone DeltaText.
function CellDelta({ value, format }) {
  if (!value) {
    return <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">same</span>
  }
  return <span className="text-[10px]"><DeltaText value={value} format={format} /></span>
}

const FORMATTERS = {
  currency: formatCurrency,
  percent: formatPercent,
  number: (v) => (v || 0).toLocaleString(),
}
const formatValue = (format, value) => (FORMATTERS[format] || FORMATTERS.number)(value || 0)

// Shared comparison state for the Summary: one entry per existing plan design
// ({ key, name, plan, metrics }), Plan A first. Every table renders one value
// column per entry; non-baseline columns carry a delta vs Plan A under each
// value. Layout adapts via CSS, not a JS width.
const CompareContext = React.createContext({ comparing: false, entries: [], exportMode: false })

const HEAD_CLASS = 'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'
/**
 * Horizontal scroller that advertises itself. When the content is wider than
 * the pane, a "scroll for more" hint appears above and a right-edge fade marks
 * the cut-off; the fade disappears once you've scrolled to the end. Nothing
 * renders when the content fits, so wide panes stay clean.
 */
function OverflowScroller({ id, className, fadeClassName = 'from-card', children }) {
  const scrollRef = React.useRef(null)
  const [overflowing, setOverflowing] = React.useState(false)
  const [atEnd, setAtEnd] = React.useState(true)

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    const update = () => {
      setOverflowing(el.scrollWidth - el.clientWidth > 1)
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    let observer = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update)
      observer.observe(el)
      if (el.firstElementChild) observer.observe(el.firstElementChild)
    }
    return () => {
      el.removeEventListener('scroll', update)
      observer?.disconnect()
    }
  }, [])

  return (
    <div id={id} className={className}>
      <div className="relative">
        <div ref={scrollRef} className="overflow-x-auto">
          {children}
        </div>
        {overflowing && !atEnd && (
          <div
            aria-hidden="true"
            className={cn('pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l to-transparent', fadeClassName)}
          />
        )}
      </div>
      {overflowing && (
        <div className="mt-1 flex justify-end">
          <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <MoveHorizontal className="h-3 w-3 shrink-0" />
            Scroll for more
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Aligned comparison table. A metric label per row with one right-aligned value
 * column per plan design. Children (CompareTableHead, CompareSubHead,
 * CompareRow, TableDivider) render their own cells straight into this one grid,
 * so every number lines up. It scrolls horizontally only if the pane is
 * genuinely too narrow for the columns, so the alignment never breaks.
 */
function CompareTable({ children, className, fill = false }) {
  const { entries } = React.useContext(CompareContext)
  const count = Math.max(1, entries.length)
  return (
    <OverflowScroller>
      <div
        className={cn(
          'grid items-baseline gap-x-8 gap-y-2.5 text-sm',
          // Default: bounded label column so values pack in right beside the labels.
          // `fill`: stretch to the container with the value columns pinned to the
          // right edge – used inside multi-column layouts so each column has no
          // dead right margin while the label↔value gap stays bounded by the column.
          fill ? 'w-full' : 'w-fit',
          className,
        )}
        style={{
          gridTemplateColumns: `${fill ? 'minmax(7rem,1fr)' : 'minmax(7rem,18rem)'} repeat(${count}, auto)`,
        }}
      >
        {children}
      </div>
    </OverflowScroller>
  )
}

// Empty trailing cells so a label-only row fills the grid's value columns.
function ValueColumnSpacers() {
  const { entries } = React.useContext(CompareContext)
  return (
    <>
      {Array.from({ length: Math.max(1, entries.length) }, (_, index) => <span key={index} />)}
    </>
  )
}

// Column header row for a CompareTable. `label` titles the first column; when
// comparing, each plan gets a named column head.
function CompareTableHead({ label = '', note = null }) {
  const { comparing, entries } = React.useContext(CompareContext)
  if (!comparing && !label && !note) return null
  return (
    <>
      <span className="flex min-w-0 flex-col">
        <span className={cn(HEAD_CLASS, 'text-foreground/70')}>{label}</span>
        {note && <span className="text-[11px] font-normal normal-case text-muted-foreground">{note}</span>}
      </span>
      {comparing ? (
        entries.map((entry) => (
          <span key={entry.key} className={cn(HEAD_CLASS, 'text-right')}>{entry.name}</span>
        ))
      ) : (
        <span />
      )}
    </>
  )
}

// Sub-group heading inside a CompareTable (e.g. "Employer contributions").
function CompareSubHead({ label, note = null }) {
  return (
    <>
      <span className="flex min-w-0 flex-col pt-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground/75">{label}</span>
        {note && <span className="text-[11px] font-normal normal-case text-muted-foreground">{note}</span>}
      </span>
      <ValueColumnSpacers />
    </>
  )
}

// Full-width rule between groups of rows inside a CompareTable.
function TableDivider() {
  return <span className="col-span-full border-t border-border/70" />
}

/**
 * One metric row inside a CompareTable: a label (with optional info tooltip and
 * secondary detail line) and one right-aligned value cell per plan design.
 * Non-baseline cells show the change vs Plan A beneath the value.
 *
 * `perPlan(entry)` may supply a per-plan `{ badge, title }` – the badge renders
 * under that plan's value (compliance screens), the title becomes hover detail.
 * When not comparing, the single plan's badge sits next to the label and its
 * detail renders under it, matching the single-plan layout.
 * `availableFor(metrics)` gates a cell to N/A per plan.
 */
// Compact "why + how to address" disclosure under a compliance row. Directional,
// modeled copy only – never a formal result or a directive (see lib/remediation.js).
function ScreenWhyFix({ remediation }) {
  const [open, setOpen] = React.useState(false)
  if (!remediation) return null
  return (
    <span className="mt-1 flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex w-fit items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
        Why / how to address
      </button>
      {open && (
        <span className="mt-1 flex flex-col gap-1 rounded-md bg-muted/50 p-2 text-xs leading-relaxed text-muted-foreground">
          <span><span className="font-medium text-foreground">Why: </span>{remediation.why}</span>
          <span><span className="font-medium text-foreground">How to address: </span>{remediation.fix}</span>
        </span>
      )}
    </span>
  )
}

function CompareRow({
  label,
  field,
  format = 'currency',
  bold = false,
  note = null,
  tooltip = null,
  detail = null,
  valueClassName = '',
  availableFor = null,
  perPlan = null,
  remediationId = null,
}) {
  const { comparing, entries, exportMode } = React.useContext(CompareContext)
  const numCell = 'text-right font-mono tabular-nums'
  const baseValue = entries[0]?.metrics?.[field] || 0
  const single = !comparing ? perPlan?.(entries[0]) : null
  const singleDetail = detail ?? (!comparing ? single?.title : null)

  return (
    <>
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          <span className={cn('min-w-0', bold ? 'font-medium text-foreground' : 'text-muted-foreground')}>{label}</span>
          {note && <span className="shrink-0 text-[11px] text-muted-foreground/70">({note})</span>}
          {!comparing && single?.badge}
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="shrink-0"><HelpCircle className="h-3.5 w-3.5 text-muted-foreground" /></TooltipTrigger>
                <TooltipContent className="max-w-xs"><p>{tooltip}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        {singleDetail && <span className="mt-0.5 text-xs text-muted-foreground">{singleDetail}</span>}
        {!comparing && !exportMode && remediationId && (
          <ScreenWhyFix remediation={buildScreenRemediation(remediationId, entries[0]?.metrics)} />
        )}
      </span>
      {entries.map((entry, index) => {
        const available = availableFor ? availableFor(entry.metrics) : true
        const info = comparing ? perPlan?.(entry) : null
        if (!available) {
          return (
            <span key={entry.key} className="flex flex-col items-end gap-0.5" title={info?.title || undefined}>
              <span className={cn(numCell, 'text-muted-foreground')}>N/A</span>
              {info?.badge}
            </span>
          )
        }
        const value = entry.metrics?.[field] || 0
        const valueClass = cn(
          numCell,
          comparing && index === 0
            ? 'text-muted-foreground'
            : bold ? 'font-semibold text-primary' : 'text-foreground',
          valueClassName,
        )
        return (
          // No min-w-0 here: the plan columns must keep their content width so
          // the table overflows into its horizontal scroller instead of the
          // no-wrap values collapsing onto each other.
          <span key={entry.key} className="flex flex-col items-end gap-0.5" title={info?.title || undefined}>
            <span className={cn(valueClass, 'whitespace-nowrap')}>{formatValue(format, value)}</span>
            {comparing && index > 0 && <CellDelta value={(value - baseValue) || null} format={format} />}
            {info?.badge}
          </span>
        )
      })}
    </>
  )
}

// A row of plain per-plan facts (text, not metrics) – plan year, safe harbor
// status. Only rendered while comparing; single-plan surfaces carry these in
// their headers already.
function PlanFactRow({ label, getValue, tooltip = null }) {
  const { comparing, entries } = React.useContext(CompareContext)
  if (!comparing) return null
  return (
    <>
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 text-muted-foreground">{label}</span>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="shrink-0"><HelpCircle className="h-3.5 w-3.5 text-muted-foreground" /></TooltipTrigger>
              <TooltipContent className="max-w-xs"><p>{tooltip}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
      {entries.map((entry) => (
        <span key={entry.key} className="text-right text-xs text-foreground">{getValue(entry)}</span>
      ))}
    </>
  )
}

// Small pill used on the compliance rows to convey modeled flags and review
// states at a glance, per plan design.
const COMPLIANCE_BADGE_TONES = {
  good: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-yellow-100 text-yellow-800',
  danger: 'bg-red-100 text-red-800',
  neutral: 'bg-muted text-muted-foreground',
}
function ComplianceBadge({ tone = 'neutral', children }) {
  if (!children) return null
  return (
    <span className={cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold', COMPLIANCE_BADGE_TONES[tone] || COMPLIANCE_BADGE_TONES.neutral)}>
      {children}
    </span>
  )
}

// Headline KPI card. Single plan: one big value with subtitle. Comparing: one
// row per plan design with the change vs Plan A beside each value.
function MetricCard({ title, field, format = 'currency', subtitle }) {
  const { comparing, entries } = React.useContext(CompareContext)
  const baseValue = entries[0]?.metrics?.[field] || 0

  return (
    <Card className="border-border/80">
      <CardContent className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        {!comparing ? (
          <>
            <p className="mt-2 text-2xl font-semibold leading-none tracking-tight tabular-nums">
              {formatValue(format, baseValue)}
            </p>
            {subtitle && <p className="mt-2 text-xs text-muted-foreground">{subtitle}</p>}
          </>
        ) : (
          <div className="mt-2 space-y-1.5">
            {entries.map((entry, index) => {
              const value = entry.metrics?.[field] || 0
              const delta = index > 0 ? (value - baseValue) || null : null
              return (
                <div key={entry.key} className="flex items-baseline justify-between gap-3">
                  <span className="shrink-0 text-xs text-muted-foreground">{entry.name}</span>
                  <span className="flex min-w-0 flex-wrap items-baseline justify-end gap-x-2">
                    <span className={cn('font-semibold tabular-nums', index === 0 ? 'text-muted-foreground' : 'text-foreground')}>
                      {formatValue(format, value)}
                    </span>
                    {index > 0 && <CellDelta value={delta} format={format} />}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function DisclosureChevron({ collapsed }) {
  return (
    <span className="ml-auto inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', collapsed && '-rotate-90')} />
    </span>
  )
}

function AcpStat({ label, value, emphasize = false }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 text-lg font-semibold tabular-nums', emphasize ? 'text-amber-700' : 'text-foreground')}>{value}</div>
    </div>
  )
}

// After-tax ACP risk. After-tax contributions are ACP-tested with match and are
// NOT exempted by safe harbor, so heavy HCE use can blow up the spread. Both
// scenarios are shown at once – the census as entered and a "what if every HCE
// maxes their after-tax to the plan cap" projection – against the informal ACP
// ceiling. Framed cautiously: it never says the plan "passes"; only a formal
// ACP test does (stated once, in the card description).
function AcpScenarioRow({ label, scenario }) {
  const over = scenario.status === 'over-basic-threshold'
  const numCell = 'py-1.5 text-right font-mono tabular-nums'
  const valClass = cn(numCell, over ? 'font-semibold text-amber-800' : 'text-foreground')
  return (
    <tr className="border-t border-border/60">
      <td className="py-1.5 pr-3 text-muted-foreground">{label}</td>
      <td className={cn(valClass, 'px-3')}>{formatPercent(scenario.hceRate)}</td>
      <td className={cn(valClass, 'px-3')}>{formatPercent(scenario.delta)}</td>
      <td className="py-1.5 pl-3 text-right">
        <ComplianceBadge tone={over ? 'warning' : 'neutral'}>{over ? 'Review' : 'No modeled flag'}</ComplianceBadge>
      </td>
    </tr>
  )
}

// Exported so the Checks stage can host it: it is a compliance screen, and the
// studio surfaces those on Checks only. The CFO view and reports still render it
// inline below, so it keeps its place in those documents.
export function AfterTaxAcpRiskCard({ data, planName = null, exportMode = false }) {
  if (!data?.available) return null

  const currentOver = data.asEntered.status === 'over-basic-threshold'
  const maxOver = data.allHceMax.status === 'over-basic-threshold'
  const anyOver = currentOver || maxOver
  const limitText = data.limit === null || data.limit === undefined ? '–' : formatPercent(data.limit)

  const verdict = currentOver
    ? 'The current after-tax elections exceed this modeled informal ACP threshold and are flagged for review. Lowering the plan’s after-tax cap, or limiting who may use it, reduces the modeled spread; a formal result depends on the administrator’s testing population and plan terms.'
    : maxOver
      ? 'Current elections are not flagged, but the HCE-max scenario exceeds the modeled threshold. The plan’s after-tax cap is a useful review point; this scenario is directional, not a formal result.'
      : 'This model does not flag a threshold crossing for either current elections or the HCE-max scenario. That is not a formal ACP determination.'

  return (
    <Card className="border-border/80">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          After-tax ACP risk{planName ? ` · ${planName}` : ''}
          <ComplianceBadge tone={anyOver ? 'warning' : 'neutral'}>
            {anyOver ? 'Flagged for review' : 'No modeled flag'}
          </ComplianceBadge>
        </CardTitle>
        <CardDescription className="text-xs">
          Employee after-tax contributions are ACP-tested (§401(m)) together with match and are not exempted by safe harbor. This is a directional screen – not a formal ACP test or a pass/fail result.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        <div className="grid grid-cols-2 gap-2">
          <AcpStat label="NHCE avg rate" value={formatPercent(data.nhceRate)} />
          <AcpStat label="Informal ACP ceiling" value={limitText} />
        </div>

        {/* Scenario table keeps its natural width; in a narrow pane it scrolls
            sideways (with the hint/fade) instead of crushing the columns. */}
        <OverflowScroller>
        <table className="w-full min-w-[26rem] text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-3 text-left font-medium">Scenario</th>
              <th className="px-3 py-1 text-right font-medium">HCE avg rate</th>
              <th className="px-3 py-1 text-right font-medium">HCE − NHCE spread</th>
              <th className="py-1 pl-3 text-right font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            <AcpScenarioRow label="Current elections" scenario={data.asEntered} />
            <AcpScenarioRow label="HCE max scenario" scenario={data.allHceMax} />
          </tbody>
        </table>
        </OverflowScroller>

        <p className={cn('text-xs leading-relaxed', anyOver ? 'text-amber-800' : 'text-muted-foreground')}>{verdict}</p>

        {data.safeHarborMatch && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            A qualifying safe-harbor match may receive deemed-satisfaction treatment for its match portion, while employee after-tax money remains subject to ACP testing. Confirm the plan&apos;s terms and operation with the administrator.
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          HCE/NHCE rates average each group&apos;s match plus after-tax as a percent of pay. The ceiling is the basic §401(m) permitted limit derived from the NHCE average. “HCE max scenario” assumes every HCE contributes after-tax up to the plan cap.
          {/* Points at an app control – meaningless in a downloaded report. */}
          {!exportMode && ' Set the plan’s after-tax cap under Employer contributions → After-tax.'}
        </p>
      </CardContent>
    </Card>
  )
}

const toBadgeNode = (screen) => (screen?.badge
  ? <ComplianceBadge tone={screen.badge.tone}>{screen.badge.text}</ComplianceBadge>
  : null)

// The compliance-screen tables, shared by the studio card and the CFO view's
// collapsed variant. Must render inside the CompareContext provider.
function ComplianceTables({ baseMetrics, comparing, showTopHeavyMin, showCashBalance, anyLtpt }) {
  const showCrossTesting = Boolean(baseMetrics?.crossTestingApplicable)
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(20rem,100%),1fr))] items-start gap-x-12 gap-y-6">
      <CompareTable fill>
        <CompareTableHead label="Indicator" />
        <CompareRow
          label="Simplified HCE/NHCE deferral comparison"
          field="adpSpread"
          format="percent"
          tooltip={`Difference between average HCE and NHCE deferral rates. This is a directional indicator only – not a formal ADP test result. HCE classification${baseMetrics?.hceEstimatedCount > 0 ? ` uses current-year compensation as a proxy for ${baseMetrics.hceEstimatedCount} employee${baseMetrics.hceEstimatedCount === 1 ? '' : 's'} (no prior-year comp provided)` : ' uses prior-year compensation'}.`}
          perPlan={(entry) => ({
            badge: null,
            title: `HCE ${formatPercent(entry.metrics?.adpHCE || 0)} / NHCE ${formatPercent(entry.metrics?.adpNHCE || 0)}${entry.metrics?.hceEstimatedCount > 0 ? ` · ${entry.metrics.hceEstimatedCount} estimated` : ''}`,
          })}
        />
        <CompareRow
          label="Informal ADP screen (HCE rate)"
          field="informalAdpHCE"
          format="percent"
          availableFor={(metrics) => Boolean(metrics?.informalAdpAvailable)}
          perPlan={(entry) => {
            const screen = buildAdpScreen(entry.metrics)
            return { badge: toBadgeNode(screen), title: screen.title }
          }}
          remediationId="adp"
          tooltip="Informal current-year ADP-style screen: average HCE base deferral rate vs the basic permitted limit derived from the NHCE average. A qualifying safe-harbor design may receive deemed-satisfaction treatment, subject to its terms and operation. This is not a formal ADP test; the administrator confirms the testing population, election, compensation definition, exclusions, corrections, and plan terms."
        />
        <CompareRow
          label="Informal ACP screen (HCE rate)"
          field="informalAcpHCE"
          format="percent"
          availableFor={(metrics) => Boolean(metrics?.informalAcpAvailable)}
          perPlan={(entry) => {
            const screen = buildAcpScreen(entry.metrics)
            return { badge: toBadgeNode(screen), title: screen.title }
          }}
          remediationId="acp"
          tooltip="Informal current-year ACP-style screen on employer match plus employee after-tax contributions. A qualifying safe-harbor match may receive deemed-satisfaction treatment for the match portion, while employee after-tax money remains subject to ACP testing. Not a formal ACP test."
        />
        <CompareRow
          label="Informal coverage ratio (§410(b))"
          field="informalCoverageRatio"
          format="percent"
          availableFor={(metrics) => Boolean(metrics?.informalCoverageAvailable)}
          perPlan={(entry) => {
            const screen = buildCoverageScreen(entry.metrics)
            return { badge: toBadgeNode(screen), title: screen.title }
          }}
          remediationId="coverage"
          tooltip="Ratio-percentage screen on the non-excludable population (employees who failed plan age/service eligibility, and LTPT deferral-only employees, are excluded). The model uses 70% as the ratio-percentage threshold; results below it require review under the average benefit test. Not a formal coverage test."
        />
        {showCrossTesting && (
          <CompareRow
            label="Cross-testing rate groups (§401(a)(4))"
            field="crossTestingFailingRateGroupCount"
            format="number"
            availableFor={(metrics) => Boolean(metrics?.crossTesting?.available)}
            perPlan={(entry) => {
              const screen = buildCrossTestingScreen(entry.metrics)
              return { badge: toBadgeNode(screen), title: screen.title }
            }}
            remediationId="cross-testing"
            tooltip="Modeled §401(a)(4) general-test screen for the employer nonelective/profit-sharing allocation: each allocation is converted to an equivalent benefit accrual rate (EBAR) and each HCE's rate group is compared with §410(b) thresholds. The model also evaluates the minimum allocation gateway. Uses documented assumptions, not the plan's actual actuarial elections. Not a formal test; confirm with the plan's actuary or TPA."
          />
        )}
        <CompareRow
          label="Estimated top-heavy indicator"
          field="topHeavyRatio"
          format="percent"
          availableFor={(metrics) => Boolean(metrics?.topHeavyAvailable)}
          perPlan={(entry) => {
            const screen = buildTopHeavyScreen(entry.metrics)
            return { badge: toBadgeNode(screen), title: screen.title }
          }}
          tooltip="A modeled ratio of 60%+ of total balances belonging to key employees can indicate potential top-heavy status. Actual status and any minimum contribution requirements should be confirmed with the plan administrator or advisor."
        />
        {showTopHeavyMin && (
          <CompareRow
            label="Top-heavy minimum shortfall"
            field="topHeavyMinShortfall"
            format="currency"
            availableFor={(metrics) => Boolean(
              metrics?.topHeavyAvailable
            )}
            perPlan={(entry) => {
              const screen = buildTopHeavyMinScreen(entry.metrics)
              return { badge: toBadgeNode(screen), title: screen.title }
            }}
            remediationId="top-heavy-min"
            tooltip="For a modeled top-heavy DC plan, each non-key participant generally needs the lesser of 3% of pay or the highest key-employee allocation rate. Modeled estimate, not a determination."
          />
        )}
        {showCashBalance && (
          <CompareRow
            label="Cash balance allocation pattern"
            field="cashBalanceHceCreditShare"
            format="percent"
            availableFor={(metrics) => Boolean(metrics?.cashBalanceEnabled && metrics?.totalCashBalanceCredits > 0)}
            perPlan={(entry) => {
              const screen = buildCashBalanceAllocationScreen(entry.metrics)
              return { badge: toBadgeNode(screen), title: screen.title }
            }}
            tooltip="Directional screen for whether cash-balance pay credits are concentrated in HCE/key employees. This is not a formal defined-benefit coverage or nondiscrimination test."
          />
        )}
      </CompareTable>

      <CompareTable fill>
        <CompareTableHead label="Indicator" />
        <PlanFactRow label="Plan year" getValue={(entry) => String(entry.plan.planYear)} />
        <PlanFactRow
          label="Safe harbor"
          getValue={(entry) => safeHarborLabel(entry.metrics?.safeHarborType)}
          tooltip="Whether the design appears configured for a safe-harbor treatment (and which kind). Actual deemed-satisfaction treatment depends on the plan's terms and operation and should be confirmed by the administrator."
        />
        {comparing && (
          <>
            <CompareRow label="Eligible employees" field="eligibleEmployeeCount" format="number" />
            <CompareRow label="Ineligible employees" field="ineligibleEmployeeCount" format="number" />
            {anyLtpt && <CompareRow label="LTPT (defer only)" field="ltptEmployeeCount" format="number" />}
            <CompareRow label="Eligibility unknown" field="unknownEligibilityEmployeeCount" format="number" />
            <TableDivider />
          </>
        )}
        <CompareRow
          label="Catch-up eligible"
          field="catchupEligibleCount"
          format="number"
          detail={comparing ? null : `${baseMetrics?.catchupUsedCount || 0} using catch-up contributions`}
        />
        <CompareRow
          label="Maxed deferrals"
          field="maxedOutCount"
          format="number"
          detail={comparing ? null : 'At the employee deferral limit (§402(g))'}
          tooltip="Employees whose own elective deferrals (pre-tax + Roth, including catch-up) reach the §402(g) employee deferral limit. This counts only what the employee defers – it does not move when an owner is filled to the §415(c) maximum with employer or after-tax money, which is shown in the next row."
        />
        <CompareRow
          label="At annual-additions max"
          field="atAnnualAdditionsLimitCount"
          format="number"
          detail={comparing ? null : 'At the §415(c) cap (deferrals + employer + after-tax)'}
          tooltip="Employees whose total annual additions – elective deferrals (excluding catch-up) plus employer contributions plus after-tax – reach the §415(c) annual-additions limit. This is where a fully-funded owner shows up even when their own deferrals are below the §402(g) limit."
        />
      </CompareTable>
    </div>
  )
}

// `cfoMode` renders the same dashboard as a building block of the CFO summary:
// the wrapper id stays with the CFO shell, and the compliance material moves
// into a single collapsed-by-default card at the bottom – CFOs reach for cost
// first and testing screens rarely.
//
// Compliance renders only in the CFO view and in exported reports. In the studio
// the Checks stage owns the screens outright (it shows the same ones with
// remediation), so repeating them under Results was a second copy of the same
// board – Results does not even summarise them, it just shows the numbers.
export default function SummaryDashboard({ cfoMode = false, exportMode = false }) {
  const planA = useStore((state) => state.planA)
  const planB = useStore((state) => state.planB)
  const planC = useStore((state) => state.planC)
  const planD = useStore((state) => state.planD)
  const analysis = useStore((state) => state.analysis)
  const setWorkspaceStage = useStore((state) => state.setWorkspaceStage)
  const activePlanKey = useStore(resolveActivePlanKey)

  const planBySlot = { planA, planB, planC, planD }
  const entries = PLAN_SLOTS
    .filter((key) => planBySlot[key])
    .map((key) => ({
      key,
      name: planBySlot[key].name || key,
      plan: planBySlot[key],
      metrics: analysis[key]?.metrics,
    }))
  const comparing = entries.length > 1
  const compareContext = { comparing, entries, exportMode }
  const baseMetrics = entries[0]?.metrics

  // The Summary still names one plan's modeled census for the compliance
  // section below: it follows the same active plan as the employee results
  // table, and says which plan when comparing.
  const bannerPlan = planBySlot[activePlanKey] || planA
  const bannerAnalysis = analysis[activePlanKey] || analysis.planA
  const bannerMetrics = bannerAnalysis?.metrics
  // CFO view only: the whole compliance block starts collapsed – it is the
  // last thing a CFO reads, so it must not push the cost story down the page.
  const [complianceCollapsed, setComplianceCollapsed] = React.useState(true)
  const toggleCompliance = () => setComplianceCollapsed((prev) => !prev)

  const {
    matchEnabled,
    necEnabled,
    necIsSafeHarbor,
    profitSharingEnabled,
  } = planA.employerContributions
  const afterTaxAllowed = Boolean(planA.afterTax?.allowed)

  const showTopHeavyMin = entries.some((entry) => (
    entry.metrics?.isTopHeavy
    && (entry.metrics?.topHeavyAvailable || entry.metrics?.cashBalanceEnabled)
  ))
  const showCashBalance = entries.some((entry) => entry.metrics?.cashBalanceEnabled)

  const getEmployerContributionsSummary = () => {
    const sources = []
    if (matchEnabled) sources.push('tiered match')
    if (necEnabled) sources.push(necIsSafeHarbor ? 'safe harbor NEC' : 'NEC')
    if (profitSharingEnabled) sources.push('profit sharing')
    if (sources.length === 0) return 'no employer contributions'
    return sources.join(' + ')
  }

  const planYearsDiffer = comparing && new Set(entries.map((entry) => entry.plan.planYear)).size > 1
  const anyLtpt = entries.some((entry) => (entry.metrics?.ltptEmployeeCount || 0) > 0)
  // This panel renders in Results, but #plan-cash-balance only exists once the
  // Plan stage is mounted. Querying it from here always missed and the optional
  // chain swallowed it, so the button silently did nothing. Switch stages first,
  // then scroll on the frame after the pane renders.
  const navigateReadiness = () => {
    setWorkspaceStage('plan')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        navigateToPlanSection('cashBalance')
        document.querySelector(`#${activePlanKey}-plan-cash-balance, #plan-cash-balance`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }

  return (
    <CompareContext.Provider value={compareContext}>
    {/* In CFO mode the #summary-dashboard anchor belongs to the CFO shell that
        hosts this dashboard, so the wrapper here stays anonymous. */}
    <div id={cfoMode ? undefined : 'summary-dashboard'} className="space-y-6">

      {isCashBalanceEnabled(bannerPlan) && (
        <PlanReadinessSummary plan={bannerPlan} planLabel={bannerPlan.name || 'Plan'} onNavigate={navigateReadiness} />
      )}

      {/* The four warning banners that used to stack here (data review,
          eligibility exclusions, advisories, plan-setting notes) plus the
          profit-sharing allocation diagnostic live on the Checks stage, grouped
          by person and resolvable in place. Results does not restate them at
          all – not even as a status line – so the numbers start at the top. */}

      {/* The output pane can be dragged genuinely narrow. The KPI cards keep a
          readable minimum width and this row scrolls sideways instead of the
          card contents squishing and wrapping. */}
      <OverflowScroller className="scroll-mt-16" id="summary-kpis" fadeClassName="from-background">
        <div className={cn('grid gap-3', comparing
          ? 'grid-cols-[repeat(auto-fit,minmax(16rem,1fr))]'
          : 'grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]')}>
        <MetricCard
          title="Employees (non-terminated)"
          field="activeEmployees"
          format="number"
          subtitle={`${baseMetrics?.totalEmployees || 0} total / ${baseMetrics?.terminatedEmployees || 0} terminated`}
        />
        <MetricCard
          title="Participation rate"
          field="participationRate"
          format="percent"
          subtitle={`${formatPercent(baseMetrics?.hceParticipationRate || 0)} HCE / ${formatPercent(baseMetrics?.nhceParticipationRate || 0)} NHCE`}
        />
        <MetricCard
          title="Avg deferral rate"
          field="avgDeferralPercent"
          format="percent"
          subtitle={`${formatPercent(baseMetrics?.avgHCEDeferralPercent || 0)} HCE / ${formatPercent(baseMetrics?.avgNHCEDeferralPercent || 0)} NHCE`}
        />
        <MetricCard
          title="Total contributions"
          field="totalContributions"
          format="currency"
          subtitle={`${formatCurrency(baseMetrics?.totalEmployeeContributions || 0)} emp / ${formatCurrency(baseMetrics?.totalEmployerContributions || 0)} employer${(baseMetrics?.totalAfterTaxContributions || 0) > 0 ? ` / ${formatCurrency(baseMetrics.totalAfterTaxContributions)} after-tax` : ''}`}
        />
        </div>
      </OverflowScroller>

      <Expandable id="summary-contributions" title="Contribution summary" showHeader={false}>
        <Card className="border-border/80">
          <CardHeader className="p-3 pb-2 pr-12">
            <CardTitle className="text-base">Contribution summary</CardTitle>
            <CardDescription>
              {comparing
                ? `${entries.length} plan designs on the same census${planYearsDiffer ? ' – plan years differ, see the Compliance table' : ` · plan year ${planA.planYear}`}`
                : `Plan year ${planA.planYear} with ${getEmployerContributionsSummary()}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 p-3 pt-0">
            {/* Contribution sources are intentionally separate visual objects.
                This prevents vesting (an employer concept) from reading as part
                of employee payroll savings while retaining every value. */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(21rem,100%),1fr))] items-stretch gap-3">
              <section className="overflow-hidden rounded-lg border border-cat-people/30 bg-card shadow-sm" aria-labelledby="employee-contribution-heading">
                <div className="flex items-start gap-2.5 border-b border-cat-people/20 bg-cat-people-soft px-3 py-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-cat-people/25 bg-background text-cat-people">
                    <UserRound className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 id="employee-contribution-heading" className="text-sm font-semibold text-foreground">Employee contributions</h3>
                    <p className="text-xs text-muted-foreground">Money employees save from their pay.</p>
                  </div>
                </div>
                <div className="p-3">
                  <CompareTable fill className="gap-y-1.5">
                    <CompareTableHead />
                    <CompareRow label="Total pre-tax" field="totalPretaxContributions" />
                    <CompareRow label="Total Roth" field="totalRothContributions" />
                    <CompareRow label="Total catch-up" field="totalCatchupContributions" />
                    <CompareRow label="After-tax contributions" field="totalAfterTaxContributions" note={afterTaxAllowed || comparing ? null : 'off in settings'} />
                    <TableDivider />
                    <CompareRow label="Total employee" field="totalEmployeeContributions" bold />
                  </CompareTable>
                </div>
              </section>

              <section className="overflow-hidden rounded-lg border border-cat-money/30 bg-card shadow-sm" aria-labelledby="employer-contribution-heading">
                <div className="flex items-start gap-2.5 border-b border-cat-money/20 bg-cat-money-soft px-3 py-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-cat-money/25 bg-background text-cat-money">
                    <Building2 className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 id="employer-contribution-heading" className="text-sm font-semibold text-foreground">Employer contributions</h3>
                    <p className="text-xs text-muted-foreground">
                      Money the company contributes for employees.
                      {!comparing && (
                        <>
                          {' · '}{baseMetrics?.eligibleEmployeeCount || 0} eligible / {baseMetrics?.ineligibleEmployeeCount || 0} ineligible
                          {baseMetrics?.ltptEmployeeCount ? ` / ${baseMetrics.ltptEmployeeCount} LTPT (defer only)` : ''}
                          {' / '}{baseMetrics?.unknownEligibilityEmployeeCount || 0} unknown
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="p-3">
                  <CompareTable fill className="gap-y-1.5">
                    <CompareTableHead />
                    <CompareSubHead label="Defined contribution" />
                    <CompareRow label="Tiered match" field="totalEmployerMatch" note={matchEnabled || comparing ? null : 'off in settings'} />
                    <CompareRow label={necIsSafeHarbor ? 'Safe harbor NEC' : 'NEC'} field="totalEmployerNec" note={necEnabled || comparing ? null : 'off in settings'} />
                    {entries.some((entry) => (entry.metrics?.totalEmployerQnec || 0) > 0) && (
                      <CompareRow label="QNEC" field="totalEmployerQnec" />
                    )}
                    <CompareRow label="Profit sharing" field="totalEmployerProfitSharing" note={profitSharingEnabled || comparing ? null : 'off in settings'} />
                    <CompareRow label="Avg per employee" field="avgEmployerContribution" />
                    <CompareRow label="Total employer (DC)" field="totalEmployerContributions" bold />

                    {showCashBalance && (
                      <>
                        <TableDivider />
                        <CompareSubHead label="Cash balance (defined benefit)" />
                        <CompareRow
                          label="Hypothetical benefit credits"
                          field="totalCashBalanceHypotheticalCredits"
                          valueClassName="text-indigo-700"
                          tooltip="Illustrative cash-balance benefit credits. They are not contributions, deductible amounts, or employer cost."
                        />
                        <CompareRow label="Total retirement-plan funding" field="totalCombinedEmployerCost" bold availableFor={(m) => Number.isFinite(m?.totalCombinedEmployerCost)} tooltip="Unavailable until an enrolled actuary supplies the required and maximum contribution range." />
                      </>
                    )}

                    <TableDivider />
                    <CompareSubHead label="Vesting & forfeitures" />
                    <CompareRow label={`Vested employer${(baseMetrics?.totalVestingUnresolved || 0) > 0 ? ' (known)' : ''}`} field="totalVestedEmployer" valueClassName="text-emerald-700" />
                    <CompareRow label={`Unvested (forfeitable)${(baseMetrics?.totalVestingUnresolved || 0) > 0 ? ' (known)' : ''}`} field="totalUnvestedEmployer" valueClassName="text-amber-700" />
                    <CompareRow label={`Est. forfeitures${(baseMetrics?.totalVestingUnresolved || 0) > 0 ? ' (known)' : ''}`} field="totalEstimatedForfeitures" valueClassName="text-red-700" />
                  </CompareTable>
                </div>
              </section>
            </div>

            <section className="rounded-lg border border-border bg-muted/30 p-3" aria-labelledby="plan-total-heading">
              <h3 id="plan-total-heading" className="mb-2 text-sm font-semibold text-foreground">Plan total</h3>
              <CompareTable fill className="gap-y-1.5">
                <CompareTableHead />
                <CompareRow label="Grand total" field="totalContributions" bold />
                <CompareRow
                  label="Illustrative max employer cost"
                  field="estimatedMaxEmployerContribution"
                  valueClassName="text-amber-700"
                  tooltip="Illustrative annual employer cost if every eligible or eligibility-unknown employee contributes enough to receive the full match. Unknown employees are modeled. Includes NEC and profit sharing."
                />
              </CompareTable>
            </section>

            {!comparing && !planA.vesting.enabled && (
              <p className="text-xs text-muted-foreground">
                Vesting is immediate (no schedule set in settings) – all employer contributions are fully vested.
              </p>
            )}
            {!comparing && (baseMetrics?.totalVestingUnresolved || 0) > 0 && (
              <p className="text-xs text-amber-700">
                {formatCurrency(baseMetrics.totalVestingUnresolved)} unresolved across {baseMetrics.vestingUnknownCount} employee{baseMetrics.vestingUnknownCount === 1 ? '' : 's'}.
                {(baseMetrics.totalForfeitureUnresolved || 0) > 0 && ` ${formatCurrency(baseMetrics.totalForfeitureUnresolved)} is associated with terminated employees and excluded from estimated forfeitures.`}
              </p>
            )}
          </CardContent>
        </Card>
      </Expandable>

      {cfoMode ? (
        <Card id="summary-compliance" className="border-border/80">
          <CardContent className="p-0">
            <button
              type="button"
              onClick={toggleCompliance}
              aria-expanded={!complianceCollapsed}
              className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border"
            >
              <span className="text-base font-semibold">Compliance indicators</span>
              {!comparing && (
                <ComplianceBadge tone={baseMetrics?.isSafeHarbor ? 'good' : 'neutral'}>
                  {safeHarborLabel(baseMetrics?.safeHarborType)}
                </ComplianceBadge>
              )}
              <DisclosureChevron collapsed={complianceCollapsed} />
            </button>
            {!complianceCollapsed && (
              <div className="space-y-5 px-4 pb-4">
                <p className="text-xs text-muted-foreground">
                  Informal, current-year screens from the loaded census – directional indicators, not formal nondiscrimination tests.
                  {comparing ? ' Each column is one plan design; hover a value for the underlying numbers.' : ''}
                  {' '}Usually the plan advisor&rsquo;s territory; included here for completeness.
                </p>
                <ComplianceTables
                  baseMetrics={baseMetrics}
                  comparing={comparing}
                  showTopHeavyMin={showTopHeavyMin}
                  showCashBalance={showCashBalance}
                  anyLtpt={anyLtpt}
                />
                <AfterTaxAcpRiskCard data={bannerMetrics?.megaBackdoorAcp} planName={comparing ? bannerPlan.name : null} exportMode={exportMode} />
              </div>
            )}
          </CardContent>
        </Card>
      ) : exportMode ? (
        <div id="summary-compliance" className="space-y-6">
          <Expandable title="Compliance indicators" showHeader={false}>
            <Card className="border-border/80">
              <CardHeader className="p-4 pb-3 pr-12">
                <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                  Compliance indicators
                  {!comparing && (
                    <ComplianceBadge tone={baseMetrics?.isSafeHarbor ? 'good' : 'neutral'}>
                      {safeHarborLabel(baseMetrics?.safeHarborType)}
                    </ComplianceBadge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  Informal, current-year screens from the loaded census – directional indicators, not formal nondiscrimination tests.
                  {comparing ? ' Each column is one plan design; hover a value for the underlying numbers.' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ComplianceTables
                  baseMetrics={baseMetrics}
                  comparing={comparing}
                  showTopHeavyMin={showTopHeavyMin}
                  showCashBalance={showCashBalance}
                  anyLtpt={anyLtpt}
                />
              </CardContent>
            </Card>
          </Expandable>

          <AfterTaxAcpRiskCard data={bannerMetrics?.megaBackdoorAcp} planName={comparing ? bannerPlan.name : null} exportMode={exportMode} />
        </div>
      ) : null}
    </div>
    </CompareContext.Provider>
  )
}
